import partnerTypeController from "@/services/partnerTypeService";
import divisionController from "@/services/divisionService";
import Swal from "sweetalert2";

export default {
  name: "PartnerTypeView",

  data() {
    return {
      items: [],
      total: 0,
      loading: false,

      divisions: [],
      loadingDivisions: false,

      page: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      totalPages: 1,

      filters: {
        search: "",
        status: "",
        mst_division_id: "",
      },

      showModal: false,
      isEdit: false,
      saving: false,
      formError: "",

      form: {
        partner_type_id: null,
        partner_type_name: "",
        mst_division_id: null,
        status: 1,
      },

      errors: {
        partner_type_name: "",
        mst_division_id: "",
      },

      toast: {
        show: false,
        message: "",
        type: "success",
      },
      toastTimer: null,
    };
  },
  computed: {
    visiblePages() {
      const total = this.totalPages;
      const current = this.page;
      const delta = 2;
      const range = [];

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(total - 1, current + delta);
        i++
      ) {
        range.push(i);
      }

      if (current - delta > 2) range.unshift("...");
      if (current + delta < total - 1) range.push("...");

      range.unshift(1);
      if (total > 1) range.push(total);

      return range;
    },
  },

  async mounted() {
    await Promise.allSettled([this.getData(), this.loadDivisions()]);
  },

  methods: {
    async loadDivisions() {
      this.loadingDivisions = true;
      try {
        const result = await divisionController.getAll({});
        this.divisions = result?.data?.data ?? result?.data ?? [];
      } catch (err) {
        this.divisions = [];
      } finally {
        this.loadingDivisions = false;
      }
    },

    getDivisionName(id) {
      const found = this.divisions.find((d) => d.mst_division_id === id);
      return found ? found.mst_division_name : "-";
    },

    async getData() {
      this.loading = true;

      const result = await partnerTypeController.getAll({});
      let filtered = result?.data?.data ?? result?.data ?? [];

      if (this.filters.search) {
        const q = this.filters.search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.partner_type_name?.toLowerCase().includes(q) ||
            item.mst_division_name?.toLowerCase().includes(q),
        );
      }

      if (this.filters.status !== "") {
        const s = parseInt(this.filters.status);
        filtered = filtered.filter((item) =>
          item.status !== undefined ? item.status == s : true,
        );
      }

      if (this.filters.mst_division_id !== "") {
        const d = parseInt(this.filters.mst_division_id);
        filtered = filtered.filter((item) => item.mst_division_id == d);
      }

      this.total = filtered.length;
      this.totalPages = Math.ceil(this.total / this.perPage) || 1;

      if (this.page > this.totalPages) this.page = this.totalPages;

      const start = (this.page - 1) * this.perPage;
      this.items = filtered.slice(start, start + this.perPage);

      this.loading = false;
    },

    changePerPage() {
      this.page = 1;
      this.getData();
    },
    changePage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.getData();
    },

    openModal() {
      this.isEdit = false;
      this.formError = "";
      this.clearErrors();
      this.form = {
        partner_type_id: null,
        partner_type_name: "",
        mst_division_id: null,
        status: 1,
      };
      this.showModal = true;
    },

    openEdit(item) {
      this.isEdit = true;
      this.formError = "";
      this.clearErrors();
      this.form = {
        partner_type_id: item.partner_type_id,
        partner_type_name: item.partner_type_name,
        mst_division_id: item.mst_division_id ?? null,
        status: item.status ?? 1,
      };
      this.showModal = true;
    },

    closeModal() {
      if (this.saving) return;
      this.showModal = false;
      this.formError = "";
      this.clearErrors();
    },

    validateForm() {
      this.clearErrors();
      let valid = true;

      if (!this.form.partner_type_name?.trim()) {
        this.errors.partner_type_name = "Nama tipe mitra tidak boleh kosong.";
        valid = false;
      } else if (this.form.partner_type_name.trim().length > 100) {
        this.errors.partner_type_name =
          "Nama tipe mitra maksimal 100 karakter.";
        valid = false;
      }
      if (!this.form.mst_division_id) {
        this.errors.mst_division_id = "Divisi wajib dipilih.";
        valid = false;
      }

      return valid;
    },

    clearErrors() {
      this.errors = {
        partner_type_name: "",
        mst_division_id: "",
      };
    },

    async saveForm() {
      if (!this.validateForm()) return;

      this.saving = true;
      this.formError = "";

      const payload = {
        partner_type_name: this.form.partner_type_name.trim(),
        mst_division_id: this.form.mst_division_id,
        status: this.form.status,
      };

      try {
        let result;

        if (this.isEdit) {
          result = await partnerTypeController.update(
            this.form.partner_type_id,
            payload,
          );
        } else {
          result = await partnerTypeController.create(payload);
        }

        if (result.success) {
          this.saving = false;
          this.closeModal();
          this.page = 1;
          await this.getData();
          this.showToast(
            this.isEdit
              ? "Tipe Partner berhasil diperbarui."
              : "Tipe Partner berhasil ditambahkan.",
            "success",
          );
        } else {
          this.formError = result.message || "Gagal menyimpan data.";
        }
      } catch (err) {
        console.error(err);
        this.formError =
          err.response?.data?.message || err.message || "Gagal menyimpan data.";
      } finally {
        this.saving = false;
      }
    },

    async toggleStatus(item) {
      const newStatus = item.status == 1 ? 0 : 1;
      const action = newStatus === 1 ? "mengaktifkan" : "menonaktifkan";
      const label = newStatus === 1 ? "diaktifkan" : "dinonaktifkan";

      const confirm = await Swal.fire({
        title: "Konfirmasi",
        text: `Yakin ingin ${action} tipe mitra ini?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Ya",
        cancelButtonText: "Batal",
        confirmButtonColor: "#198754",
      });

      if (!confirm.isConfirmed) return;

      const result = await partnerTypeController.updateStatus(
        item.partner_type_id,
        newStatus,
      );

      if (result.success) {
        this.getData();
        this.showToast(`Tipe mitra berhasil ${label}.`, "success");
      } else {
        this.showToast(result.message || "Gagal mengubah status.", "danger");
      }
    },

    showToast(message, type = "success") {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { show: true, message, type };
      this.toastTimer = setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },
  },

  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
};
